import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
} from 'lucide-react';
import { useWalkthroughStore } from '../../store/walkthroughStore';
import { useDevStore } from '../../store/useDevStore';
import { setToursEnabled } from '../../features/training/tourPrefs';
import './help.css';

function spokenText(step: {
  legenda: string;
  legendaFalada?: string | null;
}): string {
  const falada = (step.legendaFalada || '').trim();
  if (falada.length >= 12) return falada;
  return (step.legenda || '').trim();
}

export function ACWalkthrough() {
  const { settings } = useDevStore();
  const {
    steps,
    isActive,
    currentStepIndex,
    stopWalkthrough,
    nextStep,
    prevStep,
  } = useWalkthroughStore();

  if (!settings.showTour || !isActive) return null;

  const [element, setElement] = useState<Element | null>(null);
  const [spotlightStyle, setSpotlightStyle] = useState<React.CSSProperties>({});
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [clipPath, setClipPath] = useState<string>('none');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const advanceAfterAudioRef = useRef(true);

  const step = steps[currentStepIndex];
  const isLast = currentStepIndex >= steps.length - 1;

  const goNext = useCallback(() => {
    if (currentStepIndex < steps.length - 1) nextStep();
    else stopWalkthrough();
  }, [currentStepIndex, steps.length, nextStep, stopWalkthrough]);

  const stopNarration = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setProgress(0);
  }, []);

  // 1. Localizar elemento alvo
  useEffect(() => {
    setElement(null);
    if (!step?.selectorCss) return;

    const el = document.querySelector(step.selectorCss);
    if (el) {
      setElement(el);
      return;
    }

    let attempts = 0;
    const interval = setInterval(() => {
      const found = document.querySelector(step.selectorCss);
      if (found) {
        setElement(found);
        clearInterval(interval);
      }
      attempts++;
      if (attempts > 12) clearInterval(interval);
    }, 500);

    return () => clearInterval(interval);
  }, [step]);

  // 2. Scroll no alvo
  useEffect(() => {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [element]);

  // 3. Áudio real (audioUrl) apenas — sem TTS robótico/fallback
  useEffect(() => {
    stopNarration();
    if (!step?.audioUrl || !autoPlay) return;

    let cancelled = false;
    let playOnInteraction: (() => void) | null = null;

    const audio = new Audio(step.audioUrl);
    audioRef.current = audio;
    setIsPlaying(true);

    audio.play().catch(() => {
      setIsPlaying(false);
      // Fallback autoplay bypass: tenta tocar no primeiro clique na página
      playOnInteraction = () => {
        if (audioRef.current === audio && !cancelled) {
          audio.play()
            .then(() => {
              setIsPlaying(true);
              if (playOnInteraction) {
                window.removeEventListener('click', playOnInteraction);
              }
            })
            .catch(() => {});
        }
      };
      window.addEventListener('click', playOnInteraction);
    });

    audio.ontimeupdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.onended = () => {
      if (cancelled) return;
      setIsPlaying(false);
      setProgress(100);
      if (!advanceAfterAudioRef.current) return;

      const textToRead = (step.legendaFalada && step.legendaFalada.length > (step.legenda?.length || 0)
        ? step.legendaFalada
        : step.legenda) || '';
      const readingDelay = Math.max(3000, Math.min(18000, textToRead.length * 85));

      window.setTimeout(() => {
        if (cancelled) return;
        if (useWalkthroughStore.getState().isActive) goNext();
      }, readingDelay);
    };

    return () => {
      cancelled = true;
      audio.pause();
      if (playOnInteraction) {
        window.removeEventListener('click', playOnInteraction);
      }
    };
  }, [step, autoPlay, stopNarration, goNext]);

  // 4. Posicionar spotlight + tooltip (card premium mais largo)
  useEffect(() => {
    const updatePosition = () => {
      const tooltipWidth = Math.min(520, Math.max(320, window.innerWidth - 32));
      const measuredH = tooltipRef.current?.offsetHeight || 280;
      const padding = 16;
      const spotlightPadding = 6;

      if (!element) {
        setSpotlightStyle({ display: 'none' });
        setClipPath('none');
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 999999,
          width: tooltipWidth,
          maxWidth: '92vw',
        });
        return;
      }

      const rect = element.getBoundingClientRect();
      setSpotlightStyle({
        position: 'fixed',
        top: rect.top - spotlightPadding,
        left: rect.left - spotlightPadding,
        width: rect.width + spotlightPadding * 2,
        height: rect.height + spotlightPadding * 2,
        display: 'block',
      });

      const left = rect.left - spotlightPadding;
      const topOffset = rect.top - spotlightPadding;
      const right = rect.right + spotlightPadding;
      const bottom = rect.bottom + spotlightPadding;

      setClipPath(`polygon(
        0% 0%, 
        0% 100%, 
        ${left}px 100%, 
        ${left}px ${topOffset}px, 
        ${right}px ${topOffset}px, 
        ${right}px ${bottom}px, 
        ${left}px ${bottom}px, 
        ${left}px 100%, 
        100% 100%, 
        100% 0%
      )`);

      let top = rect.bottom + padding;
      let leftTooltip = rect.left + (rect.width - tooltipWidth) / 2;
      if (leftTooltip < padding) leftTooltip = padding;
      if (leftTooltip + tooltipWidth > window.innerWidth - padding) {
        leftTooltip = window.innerWidth - tooltipWidth - padding;
      }
      if (rect.bottom + padding + measuredH > window.innerHeight - 8) {
        top = Math.max(padding, rect.top - measuredH - padding);
      }

      setTooltipStyle({
        position: 'fixed',
        top: Math.max(padding, top),
        left: leftTooltip,
        width: tooltipWidth,
        maxWidth: '92vw',
        zIndex: 999999,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const layoutInterval = setInterval(updatePosition, 300);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearInterval(layoutInterval);
    };
  }, [element, step, currentStepIndex]);

  // 5. Clique no alvo → avança
  useEffect(() => {
    if (!element || !step) return;
    const handleInteraction = () => {
      setTimeout(() => {
        if (currentStepIndex < steps.length - 1) nextStep();
      }, 800);
    };
    element.addEventListener('click', handleInteraction);
    element.addEventListener('input', handleInteraction);
    return () => {
      element.removeEventListener('click', handleInteraction);
      element.removeEventListener('input', handleInteraction);
    };
  }, [element, step, currentStepIndex, steps.length, nextStep]);

  // 6. Teclado: Enter/Espaço → próximo · ESC → sair
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName || '';
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        // ainda permite ESC
        if (e.key !== 'Escape') return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        stopNarration();
        stopWalkthrough();
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        stopNarration();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isActive, goNext, stopWalkthrough, stopNarration]);

  if (!isActive || !step) return null;

  // Prefere texto narrativo conectado (legendaFalada) no card; sem TTS robótico
  const displayText =
    (step.legendaFalada && step.legendaFalada.length > (step.legenda?.length || 0)
      ? step.legendaFalada
      : step.legenda) || spokenText(step);
  const hasNarration = !!step.audioUrl;

  const handlePlayPause = () => {
    if (!step.audioUrl) return;
    if (!audioRef.current) {
      const audio = new Audio(step.audioUrl);
      audioRef.current = audio;
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      audio.onended = () => {
        setIsPlaying(false);
        setProgress(0);
        if (advanceAfterAudioRef.current) goNext();
      };
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  return (
    <>
      <div
        className="ecad-walkthrough-backdrop"
        onClick={() => {
          stopNarration();
          stopWalkthrough();
        }}
        style={{ clipPath }}
      />

      {element && (
        <div
          className="ecad-walkthrough-spotlight"
          style={{
            top: spotlightStyle.top,
            left: spotlightStyle.left,
            width: spotlightStyle.width,
            height: spotlightStyle.height,
            display: spotlightStyle.display,
          }}
        />
      )}

      <div
        ref={tooltipRef}
        className="ecad-walkthrough-tooltip ecad-walkthrough-tooltip--premium"
        style={tooltipStyle}
        role="dialog"
        aria-label="Tour guiado"
      >
        <div className="ecad-walkthrough-header">
          <span className="ecad-walkthrough-title">
            <Sparkles
              size={16}
              style={{ marginRight: 6, color: 'hsl(var(--primary))' }}
              className="animate-pulse"
            />
            {step.subtopicoTitulo || 'Instrução'}
          </span>
          <span className="ecad-walkthrough-counter">
            {currentStepIndex + 1} / {steps.length}
          </span>
          <button
            className="ecad-walkthrough-close"
            onClick={() => {
              stopNarration();
              stopWalkthrough();
            }}
            title="Fechar (Esc)"
          >
            <X size={14} />
          </button>
        </div>

        <div className="ecad-walkthrough-body">
          <p className="ecad-walkthrough-legenda">{displayText}</p>
          {!step.selectorCss && (
            <p className="ecad-walkthrough-hint">
              Visão geral desta tela — em seguida destacamos cada área.
            </p>
          )}
        </div>

        {hasNarration && (
          <div className="ecad-walkthrough-audio-bar">
            <button
              className="ecad-walkthrough-audio-btn"
              onClick={handlePlayPause}
              title={isPlaying ? 'Pausar' : 'Ouvir'}
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div className="ecad-walkthrough-audio-track">
              <div
                className="ecad-walkthrough-audio-progress"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button
              className={`ecad-walkthrough-audio-btn ${autoPlay ? 'active' : ''}`}
              onClick={() => {
                const next = !autoPlay;
                setAutoPlay(next);
                advanceAfterAudioRef.current = next;
                if (!next) stopNarration();
              }}
              title={
                autoPlay
                  ? 'Narração automática ativa (avança ao terminar)'
                  : 'Narração automática desligada'
              }
            >
              {autoPlay ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
          </div>
        )}

        <div className="ecad-walkthrough-footer">
          <button
            className="ecad-walkthrough-btn btn-outline"
            onClick={() => {
              stopNarration();
              prevStep();
            }}
            disabled={currentStepIndex === 0}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <div className="ecad-walkthrough-footer-actions">
            <span className="ecad-walkthrough-keys" title="Atalhos">
              Enter/Espaço · Esc
            </span>
            <button
              className="ecad-walkthrough-btn btn-link"
              onClick={() => {
                setToursEnabled(false);
                stopNarration();
                stopWalkthrough();
              }}
            >
              Desligar tours
            </button>
            <button
              className="ecad-walkthrough-btn btn-link"
              onClick={() => {
                stopNarration();
                stopWalkthrough();
              }}
            >
              Pular
            </button>
            {!isLast ? (
              <button
                className="ecad-walkthrough-btn btn-primary"
                onClick={() => {
                  stopNarration();
                  nextStep();
                }}
              >
                Próximo <ChevronRight size={14} />
              </button>
            ) : (
              <button
                className="ecad-walkthrough-btn btn-success"
                onClick={() => {
                  stopNarration();
                  stopWalkthrough();
                }}
              >
                Concluir
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
